import { convertDateToUTC, isValidDate } from "src/utils/time-utils";
import { ClarificationQuickFilter } from "../../containers/Quickfilters/ClarificationQuickfilter/interfaces";
import { DocumentStatusQuickFilter } from "../../containers/Quickfilters/DocumentStatusQuickfilter/interfaces";
import { LegalEntityQuickFilter } from "../../containers/Quickfilters/LegalEntityQuickfilter/interfaces";
import { PeriodQuickFilter } from "../../containers/Quickfilters/PeriodQuickfilter/interfaces";
import { TransactionQuickFilter } from "../../containers/Quickfilters/TransactionQuickfilter/interfaces";
import { Page } from "../../models/api/page";
import { SaveResponse } from "../../models/api/save/save.response";
import { DocumentDTO, DocumentsFilter, DocumentSTO } from "../../models/documents";
import { SearchOptions } from "../../models/search-options/search-options";

import { toastifyResponse } from "../../utils/toastify-response/toastify-response";
import { BaseApiService } from "../base/base-api.service";

export class DocumentsApiService extends BaseApiService<DocumentDTO, DocumentSTO> {
  constructor() {
    super({
      servicePath: "/Documents",
      loadItemsPath: "/GetDocuments",
      saveItemsPath: "/SaveDocuments",
    });
  }
}

export const loadDocuments = async (
  signal: AbortSignal | undefined = undefined,
  id: string,
  propsFilter?: {
    documentStatuses: DocumentStatusQuickFilter["documentStatuses"];
    period: PeriodQuickFilter["period"];
    legalEntities: LegalEntityQuickFilter["legalEntities"];
    transactions: TransactionQuickFilter["transactions"];
    clarifications: ClarificationQuickFilter["clarifications"];
    categoryIds?: string[];
    typeIds?: string[];
  },
  page: Page = { pageNumber: 0, pageSize: 1000 },
  searchOptions: SearchOptions = {
    query: "",
    exactMatch: false,
    deletedItems: false,
  },
  isGenericDocument?: boolean
) => {
  const service = new DocumentsApiService();

  if (isGenericDocument) {
    service.useLoadItemsPath("/GetGenericDocuments");
  }

  const response = await service.loadItems(
    {
      ids: !id ? [] : [id],
      ...(propsFilter && {
        statuses: propsFilter.documentStatuses.statuses,
        ...propsFilter.period,
        ...propsFilter.legalEntities,
        ...propsFilter.transactions,
        ...propsFilter.clarifications,
        categoryIds: propsFilter.categoryIds,
        typeIds: propsFilter.typeIds,
      }),
    } as DocumentsFilter,
    searchOptions.query,
    undefined,
    page,
    searchOptions.exactMatch,
    60,
    signal,
    isGenericDocument
  );

  toastifyResponse(response, "failed");

  return response;
};

export const loadAllDocuments = async (documentIds: string[], isGenericDocument?: boolean) => {
  const service = new DocumentsApiService();

  if (isGenericDocument) {
    service.useLoadItemsPath("/GetGenericDocuments");
  }

  const response = await service.loadItems(
    {
      ids: documentIds,
    },
    undefined,
    [],
    undefined,
    undefined,
    60,
    undefined,
    isGenericDocument
  );

  toastifyResponse(response, "failed", "success_saving_document", "failure_saving_document", "error_saving_document");

  return response;
};

export const saveDocument = async (
  document: DocumentSTO,
  correlationID?: string | null,
  notifyOnlyErrors?: boolean,
  isGenericDocument?: boolean,
  saveToCompanyAccount?: boolean
): Promise<SaveResponse> => {
  const service = new DocumentsApiService();

  if (isGenericDocument) {
    service.useSaveItemsPath("/SaveGenericDocuments");
  }

  if (saveToCompanyAccount) {
    service.useSaveItemsPath("/SaveDocumentToCompanyAccount");
  }

  const response = await service.saveItems(
    [
      {
        ...document,
        name: document.name ?? "",
        documentDate:
          document.documentDate && isValidDate(document.documentDate) ? convertDateToUTC(document.documentDate) : null,
        ...(!!isGenericDocument &&
          {
            // companyAccount: null,
          }),
      },
    ],
    undefined,
    correlationID
  );

  notifyOnlyErrors
    ? toastifyResponse(response, "failed", "failure_saving_document", "error_saving_document")
    : toastifyResponse(response, "all", "success_saving_document", "failure_saving_document", "error_saving_document");

  return response;
};

export const saveDocuments = async (
  documents: DocumentSTO[],
  correlationID?: string | null,
  isGenericDocument?: boolean
): Promise<SaveResponse> => {
  const service = new DocumentsApiService();

  if (isGenericDocument) {
    service.useSaveItemsPath("/SaveGenericDocuments");
  }

  const response = await service.saveItems(
    [
      ...documents.map((doc) => ({
        ...doc,
        documentDate: doc.documentDate ? convertDateToUTC(doc.documentDate) : null,
        ...(!!isGenericDocument && {
          companyAccount: null,
        }),
      })),
    ],
    undefined,
    correlationID
  );

  toastifyResponse(response, "all", "success_saving_document", "failure_saving_document", "error_saving_document");

  return response;
};

export const deleteDocument = async (document: DocumentSTO, isGenericDocument?: boolean): Promise<SaveResponse> => {
  const service = new DocumentsApiService();

  if (isGenericDocument) {
    service.useSaveItemsPath("/SaveGenericDocuments");
  }

  const response = await service.saveItems([{ ...document, isDeleted: true }]);

  toastifyResponse(response, "failed", undefined, "failure_saving_document", "error_saving_document");

  return response;
};

export const markDocumentsVerified = async (
  signal: AbortSignal | undefined = undefined,
  documentIDs: string[]
): Promise<SaveResponse> => {
  const docsApiService = new DocumentsApiService();
  docsApiService.useSaveItemsPath("/MarkAsVerified");

  const documentSTOs: DocumentSTO[] = documentIDs.map((id) => {
    return { id } as DocumentSTO;
  });
  const response = await docsApiService.saveItems(documentSTOs, 120);

  toastifyResponse(response, "failed");

  return response;
};
